import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaEye, FaPlus } from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import moment from "moment";
import styles from "../../../../../src/pages/schoolCockpit/master/academicConfiguration/Academiclist.module.css";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import { CustomButton } from "../../../../components";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";
import PopupModal from "../../../../components/PopupModal/PopupModal";

import useLessonPlanData from "./useLessonPlanData";
import { buildLessonPlanColumns } from "./lessonPlanColumns";
// import LessonPlanHistoryPanel from "./LessonPlanHistoryPanel";

// ══════════════════════════════════════════════════════════════════
// Pending status code — only cells matching this value are highlighted
// blue in the Reminder modal. Every other status stays white.
// Blue cells are also the ONLY cells that are clickable.
// ══════════════════════════════════════════════════════════════════
const PENDING_STATUS = 6;

// ══════════════════════════════════════════════════════════════════
// "Add" button disable threshold — when a row's generate_count hits this
// value the per-row "+" button is disabled.
// ══════════════════════════════════════════════════════════════════
const MAX_GENERATE_COUNT = 3;

// ══════════════════════════════════════════════════════════════════
// Short day labels for the reminder modal's left column
// ══════════════════════════════════════════════════════════════════
const DAY_SHORT = {
  Sunday: "Sun",
  Monday: "Mon",
  Tuesday: "Tue",
  Wednesday: "Wed",
  Thursday: "Thu",
  Friday: "Fri",
  Saturday: "Sat",
};

// ══════════════════════════════════════════════════════════════════
// Transform staff-timetable API response → ReminderModal schedule rows
// ══════════════════════════════════════════════════════════════════
const transformTimetableResponse = (timetableData) => {
  if (!timetableData || !Array.isArray(timetableData.timetable)) return [];
  return timetableData.timetable.map((dayRow) => {
    const dayName = dayRow.day_name || "";
    return {
      day: DAY_SHORT[dayName] || dayName,
      date: dayRow.date ? moment(dayRow.date).format("MMM - D") : "",
      rawDate: dayRow.date,
      periods: (dayRow.periods || []).map((p) => {
        const hasClass = p.class_name && p.subject_name;
        return {
          periodId: String(p.period),
          subject: hasClass ? p.subject_name : null,
          section: hasClass
            ? `${p.class_name}${p.section_id ? ` - ${p.section_id}` : ""}`
            : null,
          _rawStatus: p.status,
          // Preserve the full raw period so blue-cell clicks can pass it along
          _raw: p,
          _rawDate: dayRow.date,
        };
      }),
    };
  });
};

// ══════════════════════════════════════════════════════════════════
// Build period columns (I, II, III…) from the first day's period list
// ══════════════════════════════════════════════════════════════════
const buildPeriodsFromTimetable = (timetableData) => {
  if (
    !timetableData ||
    !Array.isArray(timetableData.timetable) ||
    timetableData.timetable.length === 0
  ) {
    return [];
  }
  return (timetableData.timetable[0].periods || []).map((p) => ({
    id: String(p.period),
    label: p.period_name || String(p.period),
    time:
      p.break_start_time && p.break_end_time
        ? `${p.break_start_time} - ${p.break_end_time}`
        : "",
  }));
};

// ══════════════════════════════════════════════════════════════════
// Reminder Modal (module scope — NOT inside AllLessonPlan)
// Read-only weekly timetable. Only cells where _rawStatus === 6
// (PENDING_STATUS) are highlighted blue AND clickable.
// Clicking a blue cell fires onPendingCellClick(cell, day) which the
// parent uses to navigate to /user/lessonplancreate.
// ══════════════════════════════════════════════════════════════════
const ReminderModal = ({
  open,
  onClose,
  loading,
  title,
  introText,
  periods,
  schedule,
  closeLabel,
  onPendingCellClick,
}) => {
  if (!open) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !loading) onClose?.();
  };

  const getPeriodData = (dayPeriods, periodId) => {
    if (!Array.isArray(dayPeriods)) return null;
    return (
      dayPeriods.find((p) => String(p.periodId) === String(periodId)) || null
    );
  };

  return (
    <div
      className={styles.reminderBackdrop}
      onMouseDown={handleBackdropClick}
      role="presentation"
    >
      <div
        className={styles.reminderModal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="reminder-modal-title"
      >
        <div className={styles.reminderHeader}>
          <h3 id="reminder-modal-title" className={styles.reminderTitle}>
            {title}
          </h3>
          <button
            type="button"
            className={`${styles.reminderCloseBtn} ${
              loading ? styles.reminderBtnDisabled : ""
            }`}
            onClick={onClose}
            disabled={loading}
            aria-label="Close"
          >
            <IoClose />
          </button>
        </div>

        <div className={styles.reminderBody}>
          {introText ? (
            <p className={styles.reminderIntro}>{introText}</p>
          ) : null}

          {loading ? (
            <div className={styles.reminderLoading}>Loading...</div>
          ) : periods.length === 0 || schedule.length === 0 ? (
            <div className={styles.reminderEmpty}>
              No timetable found for this week.
            </div>
          ) : (
            <div className={styles.reminderTableWrap}>
              <table className={styles.reminderTable}>
                <thead>
                  <tr className={styles.reminderHeadRow}>
                    <th
                      className={`${styles.reminderHeadCell} ${styles.reminderHeadCellFirst}`}
                    ></th>
                    {periods.map((p) => (
                      <th key={p.id} className={styles.reminderHeadCell}>
                        <span className={styles.reminderHeadPeriodLabel}>
                          {p.label}
                        </span>
                        {p.time ? (
                          <span className={styles.reminderHeadPeriodTime}>
                            {p.time}
                          </span>
                        ) : null}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {schedule.map((day) => (
                    <tr key={day.rawDate || day.day}>
                      <td className={styles.reminderDayCell}>
                        <span className={styles.reminderDayName}>
                          {day.day}
                        </span>
                        {day.date ? (
                          <span className={styles.reminderDayDate}>
                            ({day.date})
                          </span>
                        ) : null}
                      </td>
                      {periods.map((p) => {
                        const cell = getPeriodData(day.periods, p.id);
                        const hasContent = cell && cell.subject;

                        // Blue highlight ONLY when status === 6.
                        const isPending =
                          hasContent &&
                          cell._rawStatus != null &&
                          Number(cell._rawStatus) === PENDING_STATUS;

                        const cellClassName = `${styles.reminderBodyCell} ${
                          isPending ? styles.reminderPendingCell : ""
                        }`;

                        // Blue cells → clickable (navigate on click).
                        // White cells → non-interactive.
                        const handleCellClick = isPending
                          ? () => onPendingCellClick?.(cell, day)
                          : undefined;

                        return (
                          <td
                            key={`${day.day}-${p.id}`}
                            className={cellClassName}
                            onClick={handleCellClick}
                            role={isPending ? "button" : undefined}
                            tabIndex={isPending ? 0 : undefined}
                            onKeyDown={
                              isPending
                                ? (e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      onPendingCellClick?.(cell, day);
                                    }
                                  }
                                : undefined
                            }
                            aria-label={
                              isPending
                                ? `Pending lesson plan: ${cell.subject}${
                                    cell.section ? `, ${cell.section}` : ""
                                  }. Click to create.`
                                : undefined
                            }
                          >
                            {hasContent ? (
                              <>
                                <span className={styles.reminderSubjectText}>
                                  {cell.subject}
                                </span>
                                {cell.section ? (
                                  <span className={styles.reminderSectionText}>
                                    {cell.section}
                                  </span>
                                ) : null}
                              </>
                            ) : (
                              <span className={styles.reminderEmptyText}>
                                ---
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className={styles.reminderFooter}>
          <button
            type="button"
            className={`${styles.reminderBtn} ${styles.reminderCancelBtn} ${
              loading ? styles.reminderBtnDisabled : ""
            }`}
            onClick={onClose}
            disabled={loading}
          >
            {closeLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════
// Main page
// ══════════════════════════════════════════════════════════════════
function AllLessonPlan() {
  const navigate = useNavigate();
  const { translations } = useSelector((state) => state.language);
  const t = useMemo(() => translations?.labels || {}, [translations]);
  const user = TokenService.getUser();
  const roleId = Number(user?.role?.id);
  const hideActions = [4, 6].includes(roleId);

  const clientData = user?.client_data?.[0] || {};
  const schoolId =
    clientData.school_id != null && clientData.school_id !== ""
      ? Number(clientData.school_id)
      : null;
  const staffId =
    clientData.master_id != null && clientData.master_id !== ""
      ? Number(clientData.master_id)
      : null;
  const academic_id = clientData?.academic_year_id || "";

  const {
    data,
    loading,
    rowCount,
    pagination,
    setPagination,
    columnFilters,
    handleColumnFiltersChange,
    topicOptions,
    subTopicOptions,
    refetch,
  } = useLessonPlanData("", 0);

  const [selectedRow, setSelectedRow] = useState(null);

  // ─── Reminder modal state ───
  const [reminderOpen, setReminderOpen] = useState(false);
  const [reminderLoading, setReminderLoading] = useState(false);
  const [reminderPeriods, setReminderPeriods] = useState([]);
  const [reminderSchedule, setReminderSchedule] = useState([]);
  const [reminderWeekRange, setReminderWeekRange] = useState("");

  // ─── Red-dot notification indicator state ───
  const [hasNotification, setHasNotification] = useState(false);

  // ─── PopupModal state ───
  const [modalState, setModalState] = useState({
    open: false,
    type: "",
    title: "",
    message: "",
    onOk: null,
    showCancel: false,
  });

  const closeModal = () =>
    setModalState({
      open: false,
      type: "",
      title: "",
      message: "",
      onOk: null,
      showCancel: false,
    });

  // AI key must exist in campus_ai_configuration (Settings → AI Key
  // Configuration) before any create page is opened. Returns true when
  // configured; otherwise shows the popup and OK reloads this page.
  const checkAIKeyConfigured = async () => {
    try {
      const res = await AxiosInstance.get("/settings/ai/configuration");
      if (res.data?.status && res.data?.data) return true;
    } catch (err) {
      // treated as not configured
    }
    setModalState({
      open: true,
      type: "error",
      title: t.failed || "Failed",
      message:
        t.aiKeyNotConfigured ||
        "AI Key not configured. Please configure the AI Key in Settings.",
      onOk: () => window.location.reload(),
      showCancel: false,
    });
    return false;
  };

  // Top ADD button → fresh create, NO view API (Actual workflow).
  const handleAdd = async () => {
    if (await checkAIKeyConfigured()) navigate("/user/lessonplancreate");
  };

  // 👇 Per-row "+" button → carry the lesson-plan id + fromAllAdd flag so the
  //    create page triggers the lessonPlan/view API, prefills the fields, and
  //    seeds the regenerate counter from generate_count.
  const handleAddRow = async (row) => {
    if (!(await checkAIKeyConfigured())) return;
    navigate("/user/lessonplancreate", {
      state: {
        fromAllAdd: true,
        viewId: row.id, // lesson plan id used by /book/lessonPlan/view
        data: row,
      },
    });
  };

  const handleView = (row) =>
    navigate("/user/lessonplanview", {
      state: { ID: row.id, data: row },
    });

  const handleSelectRow = (rowData) => {
    if (selectedRow && selectedRow.id === rowData.id) {
      setSelectedRow(null);
    } else {
      setSelectedRow(rowData);
    }
  };

  // ─── Fetch notification flag for red dot indicator ───
  const fetchNotificationFlag = useCallback(async () => {
    if (!staffId) return;
    try {
      const res = await AxiosInstance.post("/book/lessonPlanNotification", {
        staff_id: staffId,
      });
      if (res?.data?.status && Number(res.data.is_notification) === 1) {
        setHasNotification(true);
      } else {
        setHasNotification(false);
      }
    } catch (err) {
      console.error("Error fetching lesson plan notification flag:", err);
      setHasNotification(false);
    }
  }, [staffId]);

  useEffect(() => {
    if (!hideActions) {
      fetchNotificationFlag();
    }
  }, [fetchNotificationFlag, hideActions]);

  // ─── NOTIFICATIONS button → open reminder modal + fetch timetable ───
  const handleOpenNotifications = useCallback(async () => {
    if (!schoolId || !staffId) {
      setModalState({
        open: true,
        type: "error",
        title: t.failed || "Failed",
        message:
          t.userInfoMissing ||
          "Unable to load your timetable. Please re-login and try again.",
        onOk: closeModal,
        showCancel: false,
      });
      return;
    }

    try {
      setReminderLoading(true);
      setReminderOpen(true);

      const today = dayjs().format("YYYY-MM-DD");
      const res = await AxiosInstance.post("/book/getStaffTimetable", {
        school_id: schoolId,
        staff_id: staffId,
        schedule_date: today,
        academic_year: academic_id,
      });

      if (res?.data?.status && res.data.data) {
        setReminderSchedule(transformTimetableResponse(res.data.data));
        setReminderPeriods(buildPeriodsFromTimetable(res.data.data));

        const ws = res.data.data.week_start;
        const we = res.data.data.week_end;
        if (ws && we) {
          const start = moment(ws);
          const end = moment(we);
          if (start.isSame(end, "month")) {
            setReminderWeekRange(
              `${start.format("MMMM D")}–${end.format("D, YYYY")}`,
            );
          } else if (start.isSame(end, "year")) {
            setReminderWeekRange(
              `${start.format("MMM D")} – ${end.format("MMM D, YYYY")}`,
            );
          } else {
            setReminderWeekRange(
              `${start.format("MMM D, YYYY")} – ${end.format("MMM D, YYYY")}`,
            );
          }
        } else {
          setReminderWeekRange("");
        }
      } else {
        setReminderSchedule([]);
        setReminderPeriods([]);
        setReminderWeekRange("");
      }
    } catch (err) {
      console.error("Error fetching timetable for reminder:", err);
      setReminderSchedule([]);
      setReminderPeriods([]);
      setReminderWeekRange("");
    } finally {
      setReminderLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, staffId, t]);

  const handleCloseReminder = useCallback(() => {
    if (reminderLoading) return;
    setReminderOpen(false);
  }, [reminderLoading]);

  // ─── Blue (pending) cell click → navigate to lesson-plan create ───
  const handlePendingCellClick = useCallback(
    (cell, day) => {
      if (reminderLoading) return;
      setReminderOpen(false);
      navigate("/user/lessonplancreate", {
        state: {
          fromReminder: true,
          periodInfo: cell?._raw || null,
          scheduleDate: cell?._rawDate || day?.rawDate || null,
          subject: cell?.subject || null,
          section: cell?.section || null,
        },
      });
    },
    [navigate, reminderLoading],
  );

  // ─── DELETE flow ───
  const handleDelete = () => {
    if (!selectedRow) {
      setModalState({
        open: true,
        type: "warning",
        title: t.warning || "Warning",
        message: t.selectRowToDelete || "Please select a row to delete",
        onOk: closeModal,
        showCancel: false,
      });
      return;
    }
    setModalState({
      open: true,
      type: "warning",
      title: t.delete || "Confirm Delete",
      message:
        t.confirmDeleteMessage ||
        "Are you sure you want to delete this lesson plan?",
      onOk: doDelete,
      showCancel: true,
    });
  };

  const doDelete = async () => {
    closeModal();
    if (!selectedRow) return;

    try {
      const response = await AxiosInstance.delete(`/book/lessonPlan/delete`, {
        data: {
          id: selectedRow?.id || "",
          item_id: selectedRow?.item_id || "",
          school_id: schoolId || "",
          deleted_by: user?.id || "",
        },
      });
      if (response?.data?.status) {
        setSelectedRow(null);
        setModalState({
          open: true,
          type: "success",
          title: t.success || "Success",
          message:
            response.data.message || t.deleteSuccess || "Deleted successfully",
          onOk: () => {
            closeModal();
            refetch();
          },
          showCancel: false,
        });
      } else {
        setModalState({
          open: true,
          type: "error",
          title: t.failed || "Failed",
          message: response?.data?.message || t.deleteFailed || "Delete failed",
          onOk: closeModal,
          showCancel: false,
        });
      }
    } catch (e) {
      console.error("Lesson plan delete error:", e);
      setModalState({
        open: true,
        type: "error",
        title: t.failed || "Failed",
        message: t.deleteFailed || "Delete failed",
        onOk: closeModal,
        showCancel: false,
      });
    }
  };

  const columns = useMemo(() => {
    const cols = [];

    if (!hideActions) {
      cols.push({
        id: "select",
        header: "",
        size: 40,
        Cell: ({ row }) => (
          <input
            type="checkbox"
            checked={selectedRow?.id === row.original.id}
            onChange={() => handleSelectRow(row.original)}
            className={styles.radioInput}
          />
        ),
        enableSorting: false,
        enableColumnFilter: false,
      });
    }

    cols.push(
      ...buildLessonPlanColumns({
        translations,
        topicOptions,
        subTopicOptions,
        includeSection: false,
        includeScheduledDate: false,
        includeStatus: false,
      }),
    );

    if (!hideActions) {
      cols.push({
        id: "actions",
        header: t.action || "Action",
        enableSorting: false,
        enableColumnFilter: false,
        size: 140,
        Cell: ({ row }) => {
          // Disable the "+" button when generate_count === 3
          const generateCount = Number(row.original.generate_count) || 0;
          const addDisabled = generateCount === MAX_GENERATE_COUNT;

          return (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <button
                className={styles.viewBtn}
                onClick={() => handleView(row.original)}
                title={t.view || "View"}
              >
                <FaEye />
              </button>

              {/* Per-row "+" button → prefill create via view API */}
              <button
                className={styles.viewBtn}
                onClick={() => {
                  if (addDisabled) return;
                  handleAddRow(row.original);
                }}
                disabled={addDisabled}
                title={t.add || "Add"}
                style={
                  addDisabled
                    ? { opacity: 0.45, cursor: "not-allowed" }
                    : undefined
                }
              >
                <FaPlus />
              </button>
            </div>
          );
        },
      });
    }
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translations, topicOptions, subTopicOptions, hideActions, selectedRow]);

  const reminderIntroText = useMemo(() => {
    if (reminderWeekRange) {
      return (
        t.reminderIntroStaff ||
        `The blue-highlighted periods in the timetable below indicate that your lesson plans for ${reminderWeekRange} are still pending. Click any blue cell to create its lesson plan.`
      );
    }
    return (
      t.reminderIntroStaffNoRange ||
      "The blue-highlighted periods in the timetable below indicate that your lesson plans are still pending. Click any blue cell to create its lesson plan."
    );
  }, [reminderWeekRange, t]);

  return (
    <>
      {!hideActions && (
        <div className={styles.actionsRow}>
          <CustomButton
            type="button"
            variant="contained"
            className={styles.addButton}
            fullWidth={false}
            onClick={handleAdd}
          >
            {t.add || "ADD"}
          </CustomButton>
          <CustomButton
            type="button"
            variant="contained"
            className={styles.addButton}
            fullWidth={false}
            onClick={() => navigate("/user/schedulelist")}
          >
            {t.schedule || "SCHEDULE"}
          </CustomButton>

          {/* ═════ NOTIFICATIONS button with red-dot indicator ═════ */}
          <span className={styles.notificationWrapper}>
            <CustomButton
              type="button"
              variant="contained"
              className={styles.addButton}
              fullWidth={false}
              onClick={handleOpenNotifications}
            >
              {t.notifications || "NOTIFICATIONS"}
            </CustomButton>
            {hasNotification && (
              <span
                className={styles.notificationRedDot}
                aria-label="New notifications available"
              />
            )}
          </span>

          <CustomButton
            type="button"
            variant="contained"
            className={styles.deleteButton}
            fullWidth={false}
            onClick={handleDelete}
          >
            {t.delete || "DELETE"}
          </CustomButton>
        </div>
      )}

      <CustomMRTTable
        columns={columns}
        data={data}
        loading={loading}
        pagination={pagination}
        rowCount={rowCount}
        onPaginationChange={setPagination}
        columnFilters={columnFilters}
        onColumnFiltersChange={handleColumnFiltersChange}
        enableColumnFilters={true}
        enableGlobalFilter={false}
        hideGlobalSearch={true}
        enableExpanding={false}
        // renderDetailPanel={({ row }) => <LessonPlanHistoryPanel row={row} />}
      />

      {/* ════════════ Reminder Modal (NOTIFICATIONS button) ════════════ */}
      <ReminderModal
        open={reminderOpen}
        onClose={handleCloseReminder}
        loading={reminderLoading}
        title={t.reminder || "Reminder"}
        introText={reminderIntroText}
        periods={reminderPeriods}
        schedule={reminderSchedule}
        closeLabel={t.close || "Close"}
        onPendingCellClick={handlePendingCellClick}
      />

      <PopupModal
        open={modalState.open}
        type={modalState.type}
        title={modalState.title}
        message={modalState.message}
        confirmText={t.ok || "OK"}
        cancelText={t.cancel || "Cancel"}
        onOk={modalState.onOk || closeModal}
        onClose={closeModal}
        showCancel={modalState.showCancel}
      />
    </>
  );
}

export default AllLessonPlan;
